import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Check, Copy, Download, FileCheck2, Loader2, PenLine, Search, Send } from "lucide-react";

export default function ReferralSheetsView() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [activeSid, setActiveSid] = useState(null);
    const [sheet, setSheet] = useState(null);
    const [sheetLoading, setSheetLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let active = true;
        api.get("/admin/passport/sessions").then((r) => {
            if (!active) return;
            setSessions(r.data || []);
        }).catch(() => {
            toast.error("Couldn't load sessions.");
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, []);

    const openSheet = async (sid) => {
        setActiveSid(sid);
        setSheet(null);
        setSheetLoading(true);
        try {
            const r = await api.get(`/admin/passport/${encodeURIComponent(sid)}/sheet`);
            setSheet(r.data);
        } catch (e) {
            toast.error("Couldn't load referral sheet.");
        } finally {
            setSheetLoading(false);
        }
    };

    const filtered = sessions.filter((s) => {
        const t = (search || "").toLowerCase();
        if (!t) return true;
        return (
            (s.session_id || "").toLowerCase().includes(t) ||
            (s.patient_name || "").toLowerCase().includes(t) ||
            (s.caregiver_name || "").toLowerCase().includes(t)
        );
    });

    const composedText = sheet ? buildReferralText(sheet) : "";

    const copyAll = async () => {
        try {
            await navigator.clipboard.writeText(composedText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success("Referral text copied — paste into your email.");
        } catch {
            toast.error("Couldn't copy. Select-all + copy from the box.");
        }
    };

    const emailDraft = () => {
        if (!sheet) return;
        const subject = encodeURIComponent(`GUIDE referral — ${sheet.passport?.patient_name || "patient"}`);
        const body = encodeURIComponent(composedText);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    return (
        <section data-testid="admin-referral-sheets">
            <header className="mb-6">
                <h1 className="font-heading text-2xl md:text-3xl text-[#12776F]">GUIDE referral sheets</h1>
                <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                    Pick a caregiver session to assemble a one-page referral for a GUIDE provider — passport,
                    all screener scores, flagged items, and uploaded documents. Copy or email it directly.
                </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                <aside className="lg:col-span-4">
                    <div className="ml-card p-4 bg-white">
                        <label className="block">
                            <span className="sr-only">Search sessions</span>
                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7A74]" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search session, patient, caregiver…"
                                    data-testid="referral-search"
                                    className="w-full pl-9 pr-3 py-2 rounded-full bg-cream-light border border-cream-dark text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                />
                            </div>
                        </label>

                        {loading ? (
                            <p className="text-sm text-[#6E7A74] mt-4 inline-flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" /> Loading…
                            </p>
                        ) : filtered.length === 0 ? (
                            <p className="text-sm text-[#6E7A74] mt-4">No sessions match.</p>
                        ) : (
                            <ul className="mt-3 space-y-1 max-h-[60vh] overflow-y-auto" data-testid="referral-sessions">
                                {filtered.map((s) => {
                                    const active = activeSid === s.session_id;
                                    return (
                                        <li key={s.session_id}>
                                            <button
                                                type="button"
                                                onClick={() => openSheet(s.session_id)}
                                                data-testid={`referral-session-${s.session_id}`}
                                                className={`w-full text-left px-3 py-2 rounded-2xl border ${active
                                                    ? "bg-teal text-white border-teal"
                                                    : "bg-white text-[#1F2624] border-cream-dark hover:border-teal-light"
                                                    }`}
                                            >
                                                <div className="text-sm font-medium truncate">
                                                    {s.patient_name || s.session_id}
                                                </div>
                                                <div className={`text-xs truncate ${active ? "text-white/80" : "text-[#6E7A74]"}`}>
                                                    {s.caregiver_name || "—"} ·{" "}
                                                    {s.has_passport && "passport · "}
                                                    {s.has_screeners && "screeners · "}
                                                    {s.session_id}
                                                </div>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </aside>

                <div className="lg:col-span-8">
                    {/* "Blank template" CTA — for ad-hoc referrals where
                        the family isn't a Memory Lane user (yet). Gives
                        Ashlee a one-click, fill-in-the-blank packet she
                        can copy/email like any other referral. */}
                    {!activeSid && (
                        <div className="ml-card p-5 md:p-6 mb-4 bg-yellow-soft/40 border border-yellow flex items-start gap-4" data-testid="referral-blank-template-cta">
                            <div className="w-11 h-11 rounded-2xl bg-yellow text-yellow-dark flex items-center justify-center shrink-0">
                                <FileCheck2 size={18} strokeWidth={1.7} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#1F2624]">No Memory Lane record for this family?</p>
                                <p className="text-xs text-[#4C5551] mt-0.5">
                                    Open a blank referral template and fill it in by hand — same format as the
                                    Memory Lane-generated sheets, ready to copy or email.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSheet(BLANK_REFERRAL_SHEET)}
                                data-testid="referral-blank-template-open"
                                className="ml-btn-secondary !min-h-[40px] !py-2 !px-4 !text-sm inline-flex items-center gap-1.5 shrink-0"
                            >
                                <PenLine size={14} strokeWidth={1.7} /> Open blank template
                            </button>
                        </div>
                    )}

                    {!activeSid && !sheet ? (
                        <div className="ml-card p-8 bg-cream-light text-center" data-testid="referral-empty">
                            <FileCheck2 size={28} strokeWidth={1.6} className="text-teal-dark mx-auto mb-2" />
                            <p className="text-sm text-[#4C5551]">Pick a session on the left to build a referral sheet.</p>
                        </div>
                    ) : sheetLoading ? (
                        <p className="text-sm text-[#6E7A74] inline-flex items-center gap-2">
                            <Loader2 size={14} className="animate-spin" /> Loading sheet…
                        </p>
                    ) : sheet ? (
                        sheet.is_blank_template
                            ? <BlankReferralSheet />
                            : <ReferralSheet sheet={sheet} composedText={composedText} onCopy={copyAll} copied={copied} onEmail={emailDraft} />
                    ) : null}
                </div>
            </div>
        </section>
    );
}

/* Blank referral template — used when Ashlee needs to draft a referral
 * for a family who isn't on Memory Lane yet (a walk-in, a hospital
 * hand-off, etc.). Same shape as ReferralSheet expects so the existing
 * copy/email/format machinery works without branching. */
const BLANK_REFERRAL_SHEET = {
    session_id: "(no Memory Lane session)",
    generated_at: new Date().toISOString(),
    is_blank_template: true,
    passport: {
        patient_name: "",
        caregiver_name: "",
        eligibility_notes: "",
        caregiver_goals: "",
        behavioral_history: "",
        interventions_tried: "",
        safety_concerns: "",
        care_preferences: "",
        notes: "",
    },
    eligibility: {
        answered: false,
        score: null,
        band: "",
        flag: "",
        blockers: [],
    },
    score_rows: [],
    key_flags: [],
    uploaded_documents: [],
};

function ReferralSheet({ sheet, composedText, onCopy, copied, onEmail }) {
    const p = sheet.passport || {};
    const e = sheet.eligibility || {};
    const rows = sheet.score_rows || [];
    const flags = sheet.key_flags || [];
    const ups = sheet.uploaded_documents || [];

    return (
        <div className="space-y-4" data-testid="referral-sheet">
            <div className="ml-card p-5 md:p-6 bg-white">
                <div className="flex items-start gap-3 justify-between flex-wrap mb-2">
                    <div>
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">GUIDE referral sheet</p>
                        <h2 className="font-heading text-2xl text-[#12776F]">
                            {p.patient_name || "(no patient name)"}{" "}
                            <span className="text-base text-[#6E7A74]">· {sheet.session_id}</span>
                        </h2>
                        <p className="text-xs text-[#6E7A74] mt-0.5">Generated {new Date(sheet.generated_at).toLocaleString()}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={onCopy}
                            data-testid="referral-copy"
                            className="ml-btn-secondary inline-flex items-center gap-2"
                        >
                            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy as text</>}
                        </button>
                        <button
                            type="button"
                            onClick={onEmail}
                            data-testid="referral-email"
                            className="ml-btn-primary inline-flex items-center gap-2"
                        >
                            <Send size={14} /> Open in email
                        </button>
                    </div>
                </div>
            </div>

            <div className={`ml-card p-5 md:p-6 border ${e.band === "Likely eligible"
                ? "bg-stage_early/30 border-stage_early"
                : "bg-cream-light border-cream-dark"
                }`}>
                <h3 className="font-heading text-lg text-[#1F2624] inline-flex items-center gap-2 mb-1">
                    <FileCheck2 size={16} className="text-stage_early-dark" /> GUIDE eligibility
                </h3>
                {e.answered ? (
                    <>
                        <p className="text-sm text-[#1F2624] font-medium">{e.band}</p>
                        {e.score && <p className="text-xs text-[#6E7A74] mt-0.5">Score: {e.score}</p>}
                        {(e.blockers || []).length > 0 && (
                            <p className="text-xs text-coral-dark mt-2">Blockers: {(e.blockers || []).join("; ")}</p>
                        )}
                    </>
                ) : (
                    <p className="text-sm text-[#4C5551]">Caregiver hasn&apos;t completed the eligibility screener.</p>
                )}
            </div>

            {flags.length > 0 && (
                <div className="ml-card p-5 md:p-6 bg-coral-light border border-coral-softer">
                    <h3 className="font-heading text-lg text-[#1F2624] inline-flex items-center gap-2 mb-2">
                        <AlertTriangle size={16} className="text-coral-dark" /> Flagged for clinician review
                    </h3>
                    <ul className="text-sm text-[#1F2624] space-y-1 list-disc pl-5">
                        {flags.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                </div>
            )}

            <div className="ml-card p-5 md:p-6 bg-white" data-testid="referral-scores">
                <h3 className="font-heading text-lg text-[#1F2624] mb-3">All screener scores</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                                <th className="py-2 pr-4">Screener</th>
                                <th className="py-2 pr-4">Score</th>
                                <th className="py-2">Band</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className={`border-b border-cream-dark ${r.flag ? "bg-coral-light/40" : ""}`}>
                                    <td className="py-2 pr-4 text-[#1F2624]">{r.title}</td>
                                    <td className="py-2 pr-4 text-[#1F2624] font-mono text-xs">{r.score ?? "—"}{r.max_score ? ` / ${r.max_score}` : ""}</td>
                                    <td className="py-2 text-[#4C5551]">{r.band || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="ml-card p-5 md:p-6 bg-white" data-testid="referral-passport">
                <h3 className="font-heading text-lg text-[#1F2624] mb-3">Continuity passport</h3>
                {[
                    ["caregiver_goals", "Caregiver goals"],
                    ["behavioral_history", "Behavioral history"],
                    ["interventions_tried", "Interventions already tried"],
                    ["major_concerns", "Major concerns"],
                    ["care_preferences", "Care preferences"],
                    ["summary", "Summary"],
                    ["eligibility_notes", "Eligibility notes"],
                ].map(([k, label]) => (
                    p[k] ? (
                        <div key={k} className="mb-3">
                            <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">{label}</p>
                            <p className="text-sm text-[#1F2624] whitespace-pre-wrap leading-relaxed">{p[k]}</p>
                        </div>
                    ) : null
                ))}
                {!Object.keys(p).some((k) => p[k] && !["session_id", "created_at", "updated_at", "user_id"].includes(k)) && (
                    <p className="text-sm text-[#6E7A74] italic">Caregiver hasn&apos;t written a passport yet.</p>
                )}
            </div>

            {ups.length > 0 && (
                <div className="ml-card p-5 md:p-6 bg-white" data-testid="referral-uploads">
                    <h3 className="font-heading text-lg text-[#1F2624] mb-2">Uploaded legal documents</h3>
                    <ul className="text-sm text-[#1F2624] space-y-1">
                        {ups.map((u) => (
                            <li key={u.id}>· {u.label || u.original_filename} <span className="text-xs text-[#6E7A74]">({u.kind})</span></li>
                        ))}
                    </ul>
                </div>
            )}

            <details className="ml-card p-5 md:p-6 bg-cream-light">
                <summary className="font-heading text-lg text-[#1F2624] cursor-pointer">Composed referral text (what gets copied)</summary>
                <pre
                    data-testid="referral-composed-text"
                    className="mt-3 text-xs whitespace-pre-wrap text-[#1F2624] bg-white border border-cream-dark rounded-2xl p-4 max-h-[40vh] overflow-y-auto"
                >{composedText}</pre>
            </details>
        </div>
    );
}

function BlankField({ label, k, multiline = false, placeholder = "", value, onSet }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">{label}</span>
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => onSet(k, e.target.value)}
                    placeholder={placeholder}
                    data-testid={`referral-blank-field-${k}`}
                    rows={3}
                    className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                />
            ) : (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onSet(k, e.target.value)}
                    placeholder={placeholder}
                    data-testid={`referral-blank-field-${k}`}
                    className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                />
            )}
        </label>
    );
}

function BlankReferralSheet() {
    // Local-only state — the blank template never persists. Ashlee fills
    // it in, copies to clipboard / emails, and moves on. No DB write.
    const [draft, setDraft] = useState({
        patient_name: "",
        caregiver_name: "",
        caregiver_phone: "",
        eligibility_band: "",
        eligibility_notes: "",
        caregiver_goals: "",
        behavioral_history: "",
        interventions_tried: "",
        major_concerns: "",
        safety_concerns: "",
        care_preferences: "",
        medications_summary: "",
        recent_changes: "",
        summary: "",
    });
    const [copied, setCopied] = useState(false);

    const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const composed = (() => {
        const lines = [];
        lines.push(`GUIDE REFERRAL — ${draft.patient_name || "patient"}`);
        lines.push(`Drafted ${new Date().toLocaleString()} via Memory Lane (manual template)`);
        if (draft.caregiver_name) {
            lines.push(`Caregiver: ${draft.caregiver_name}${draft.caregiver_phone ? ` · ${draft.caregiver_phone}` : ""}`);
        }
        lines.push("");
        lines.push("ELIGIBILITY");
        lines.push(`  ${draft.eligibility_band || "(Screener not completed.)"}`);
        if (draft.eligibility_notes) lines.push(`  Notes: ${draft.eligibility_notes}`);
        lines.push("");
        const sections = [
            ["CAREGIVER GOALS", draft.caregiver_goals],
            ["BEHAVIORAL HISTORY", draft.behavioral_history],
            ["INTERVENTIONS ALREADY TRIED", draft.interventions_tried],
            ["MAJOR CONCERNS", draft.major_concerns],
            ["SAFETY CONCERNS", draft.safety_concerns],
            ["CARE PREFERENCES", draft.care_preferences],
            ["MEDICATIONS SUMMARY", draft.medications_summary],
            ["RECENT CHANGES (14 days)", draft.recent_changes],
            ["SUMMARY", draft.summary],
        ];
        for (const [title, body] of sections) {
            if (!body) continue;
            lines.push(title);
            lines.push("  " + body.replace(/\n/g, "\n  "));
            lines.push("");
        }
        lines.push("—");
        lines.push("Memory Lane Care Navigator · memorylanecare.net");
        return lines.join("\n");
    })();

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(composed);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success("Referral text copied.");
        } catch {
            toast.error("Couldn't copy. Select-all + copy from the preview.");
        }
    };
    const email = () => {
        const subject = encodeURIComponent(`GUIDE referral — ${draft.patient_name || "patient"}`);
        const body = encodeURIComponent(composed);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    return (
        <div className="space-y-4" data-testid="referral-blank-sheet">
            <div className="ml-card p-5 md:p-6 bg-white">
                <div className="flex items-start gap-3 justify-between flex-wrap mb-3">
                    <div>
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Blank GUIDE referral · manual draft</p>
                        <h2 className="font-heading text-2xl text-[#12776F]">
                            {draft.patient_name || <span className="text-[#6E7A74]">(enter patient name below)</span>}
                        </h2>
                        <p className="text-xs text-[#6E7A74] mt-0.5">Drafted {new Date().toLocaleString()}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={copy}
                            data-testid="referral-blank-copy"
                            className="ml-btn-secondary inline-flex items-center gap-2"
                        >
                            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy as text</>}
                        </button>
                        <button
                            type="button"
                            onClick={email}
                            data-testid="referral-blank-email"
                            className="ml-btn-primary inline-flex items-center gap-2"
                        >
                            <Send size={14} /> Open in email
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    const res = await api.post("/admin/blank-referral.pdf", draft, { responseType: "blob" });
                                    const blob = new Blob([res.data], { type: "application/pdf" });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url;
                                    const safe = (draft.patient_name || "patient")
                                        .replace(/[^A-Za-z0-9-_ ]/g, "")
                                        .replace(/\s+/g, "-")
                                        .toLowerCase() || "patient";
                                    a.download = `guide-referral-${safe}.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    URL.revokeObjectURL(url);
                                    toast.success("Referral PDF downloaded.");
                                } catch {
                                    toast.error("Couldn't generate the PDF.");
                                }
                            }}
                            data-testid="referral-blank-pdf"
                            className="ml-btn-primary inline-flex items-center gap-2"
                        >
                            <Download size={14} /> Download as PDF
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <BlankField label="Patient name" k="patient_name" placeholder="e.g. Marie Johnson" value={draft.patient_name} onSet={setField} />
                    <BlankField label="Caregiver name" k="caregiver_name" placeholder="e.g. Sam Johnson" value={draft.caregiver_name} onSet={setField} />
                    <BlankField label="Caregiver phone / email" k="caregiver_phone" placeholder="(415) 555-…" value={draft.caregiver_phone} onSet={setField} />
                    <BlankField label="GUIDE eligibility (band)" k="eligibility_band" placeholder="Likely eligible / Borderline / Not eligible" value={draft.eligibility_band} onSet={setField} />
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3">
                    <BlankField label="Eligibility notes / blockers" k="eligibility_notes" multiline placeholder="e.g. On Medicare Advantage — see if disenrolment possible." value={draft.eligibility_notes} onSet={setField} />
                    <BlankField label="Caregiver goals" k="caregiver_goals" multiline placeholder="What does the family want most?" value={draft.caregiver_goals} onSet={setField} />
                    <BlankField label="Behavioral history" k="behavioral_history" multiline placeholder="Sundowning starting 2 months ago, intermittent wandering at night…" value={draft.behavioral_history} onSet={setField} />
                    <BlankField label="Interventions already tried" k="interventions_tried" multiline placeholder="Trazodone titration, redirecting with music, lavender oil…" value={draft.interventions_tried} onSet={setField} />
                    <BlankField label="Major concerns" k="major_concerns" multiline placeholder="Caregiver burnout, falls risk, refusal of bathing…" value={draft.major_concerns} onSet={setField} />
                    <BlankField label="Safety concerns" k="safety_concerns" multiline placeholder="Wandering, driving, stove use, firearms…" value={draft.safety_concerns} onSet={setField} />
                    <BlankField label="Care preferences" k="care_preferences" multiline placeholder="Cultural / religious / comfort wishes…" value={draft.care_preferences} onSet={setField} />
                    <BlankField label="Medications summary" k="medications_summary" multiline placeholder="Donepezil 10mg qhs, Memantine 10mg BID, Trazodone 50mg qhs…" value={draft.medications_summary} onSet={setField} />
                    <BlankField label="Recent changes (14 days)" k="recent_changes" multiline placeholder="Anything new — medications, behaviors, function…" value={draft.recent_changes} onSet={setField} />
                    <BlankField label="Summary for the receiving provider" k="summary" multiline placeholder="One-paragraph close: what this family needs most from a GUIDE provider." value={draft.summary} onSet={setField} />
                </div>
            </div>

            <div className="ml-card p-5 md:p-6 bg-cream-light" data-testid="referral-blank-preview">
                <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-2">
                    Live preview · what gets copied/emailed
                </p>
                <pre
                    className="text-xs text-[#1F2624] whitespace-pre-wrap font-mono bg-white border border-cream-dark rounded-2xl p-4 max-h-[400px] overflow-auto"
                    data-testid="referral-blank-preview-text"
                >{composed}</pre>
            </div>
        </div>
    );
}


function buildReferralText(sheet) {
    const p = sheet.passport || {};
    const e = sheet.eligibility || {};
    const rows = sheet.score_rows || [];
    const flags = sheet.key_flags || [];
    const ups = sheet.uploaded_documents || [];

    const lines = [];
    lines.push(`GUIDE REFERRAL — ${p.patient_name || "patient"} (session ${sheet.session_id})`);
    lines.push(`Generated ${new Date(sheet.generated_at).toLocaleString()} via Memory Lane Care Navigator`);
    if (p.caregiver_name) lines.push(`Caregiver: ${p.caregiver_name}`);
    lines.push("");
    lines.push("ELIGIBILITY");
    if (e.answered) {
        lines.push(`  ${e.band}${e.score ? ` (score ${e.score})` : ""}`);
        if ((e.blockers || []).length) lines.push(`  Blockers: ${e.blockers.join("; ")}`);
    } else {
        lines.push("  (Screener not completed.)");
    }
    if (p.eligibility_notes) lines.push(`  Notes: ${p.eligibility_notes}`);
    lines.push("");
    if (flags.length) {
        lines.push("KEY FLAGS");
        flags.forEach((f) => lines.push(`  • ${f}`));
        lines.push("");
    }
    lines.push("SCREENER SCORES");
    rows.forEach((r) => {
        const score = r.score ?? "—";
        const max = r.max_score ? ` / ${r.max_score}` : "";
        lines.push(`  ${r.title}: ${score}${max}${r.band ? ` — ${r.band}` : ""}${r.flag ? "  [FLAG]" : ""}`);
    });
    lines.push("");
    lines.push("CONTINUITY PASSPORT");
    [
        ["caregiver_goals", "Caregiver goals"],
        ["behavioral_history", "Behavioral history"],
        ["interventions_tried", "Interventions tried"],
        ["major_concerns", "Major concerns"],
        ["care_preferences", "Care preferences"],
        ["summary", "Summary"],
    ].forEach(([k, label]) => {
        if (p[k]) {
            lines.push(`  ${label}:`);
            lines.push(`    ${p[k].split("\n").join("\n    ")}`);
            lines.push("");
        }
    });
    if (ups.length) {
        lines.push("UPLOADED DOCUMENTS");
        ups.forEach((u) => lines.push(`  · ${u.label || u.original_filename} (${u.kind})`));
        lines.push("");
    }
    lines.push("— Ashlee Sherrod, Memory Lane Care Navigator");
    lines.push("ashlee@asvmedicalservices.com · memorylanecare.net");
    return lines.join("\n");
}


